import _ from "lodash";
import numeral from "numeral";
import { useMemo } from "react";
import { useBalanceStore } from "../../../_store/balanceStore";
import { useRetireStore } from "../../../_store/retireStore";
import { useStrategyStore } from "../../../_store/strategyRetireStore";
import { useTaxStore } from "../../../_store/taxplanStore";
import LineChartMain from "../../../components/chart/LineChartMain";
import { ImageMain } from "../../../components/img/Img.Main";
import {
  InvestmentReturnCalAfter,
  InvestmentReturnCalSensitive,
  InvestmentReturnCalSensitiveNormal,
} from "../../../functions/retirement/retirementFunctionAfter";
import { cn } from "../../../utils/useFunctions";
import {
  ICON_CHECK,
  ICON_CLOSE,
  ICON_STAR,
  ICON_WARNING,
} from "../../../utils/useIcons";
import { useIsMobile } from "../../../utils/useIsMobile";
import { calPMT, calPMTFVA } from "../../education/functions/function";
import { avgWeight } from "../../retirement_plan/functions/functionMain";
import {
  lastResultInvestmentAssetsAFSensitive,
  loopInvestmentAssetsAFSensitive,
} from "../../retirement_plan/functions/functionRetrementSensitivePlan";
import {
  dataAgeAF,
  loopInvestmentAssets,
} from "../../retirement_plan/functions/functionRetriementPlan";
import { calculateTax, checkSum } from "../../tax_plan/functions";
import { PdfCard, PdfCardWhite } from "../component/pdf.c.card";
import PdfFooter from "../component/pdf.c.footer";
import PdfHeader from "../component/pdf.c.header";
import { PdfHeaderText } from "../component/pdf.c.headerText";
import PdfLine from "../component/pdf.c.line";
import { STYLE_PAGE, STYLE_PDF_TEXT_MEDIUM_11 } from "../styles/pdf.style";
import { useEducationStore } from "../../../_store/educationStore";

function PdfPageRetireThree({ page, userData }) {
  const balanceData = useBalanceStore((state) => state.balanceData);
  const retireData = useRetireStore((state) => state.retireData);
  const taxData = useTaxStore((state) => state.taxData);
  const strategy = useStrategyStore((state) => state.strategy);
  const educationData = useEducationStore((state) => state.educationData);

  const strategyLists = strategy?.map((item) => item?.key);


  const getCaltax = () => {
    let result = 0;
    result =
      taxData?.tax_income -
      taxData?.deduction_expenses -
      taxData?.deduct_summary;
    return calculateTax(result);
  };

  const displaySumAfterTaxPaid = () => {
    let result = 0;
    result =
      Number(taxData?.tax_income) -
      Number(taxData?.deduction_expenses) -
      Number(taxData?.deduct_summary_af);

    return calculateTax(result);
  };

  const displayTaxPaid = useMemo(
    () => calculateTax(taxData?.tax_income),
    [taxData?.tax_income]
  );

  const displaySumTax = useMemo(
    () => checkSum(getCaltax(), taxData?.after_tax_paid, 0, 0),
    [getCaltax(), taxData?.after_tax_paid, 0, 0]
  );

  const checkPercent = () => {
    let result = 0;
    result = ((getCaltax() - taxData?.after_tax_paid) / getCaltax()) * 100;
    if (isNaN(result)) {
      return 0;
    }
    return Number(result)?.toFixed(2);
  };

  const dataPlanBf = retireData?.bf_investment_assets?.map(
    (bf_investment_assets) => {
      return bf_investment_assets?.results;
    }
  );

  // หนี้สินต่อสินทรัพย์
  const valueDebttoTotalAsset = useMemo(() => {
    let sum = 0;
    if (balanceData?.debt_propotion) {
      sum =
        balanceData?.property_propotion > 0
          ? (balanceData?.debt_propotion / balanceData?.property_propotion) *
            100
          : 0;
    }
    return sum;
  }, [balanceData]);
  const getDebttoTotalAssetStatus = () => {
    if (!balanceData?.debt_propotion) return;

    if (valueDebttoTotalAsset > 50) {
      return "bad";
    }
    return "good";
  };

  // ชำระหนี้ต่อรายรับ
  const valueDebttoSalary = useMemo(() => {
    let sum = 0;
    const debtPermonth =
      balanceData?.sum_debt_short_permonth +
      balanceData?.sum_debt_long_permonth;
    const salaryPermonth = balanceData?.salary_propotion
      ? balanceData?.salary_propotion / 12
      : 0;
    sum = salaryPermonth > 0 ? (debtPermonth / salaryPermonth) * 100 : 0;
    return sum;
  }, [balanceData]);
  const getDebttoSalaryStatus = () => {
    if (valueDebttoSalary > 50) {
      return "bad";
    }
    return "good";
  };

  // การออม
  const valueConservation = useMemo(() => {
    let sum = 0;
    if (balanceData?.sum_expenses_invest_saving) {
      sum = balanceData?.sum_expenses_invest_saving;
    }
    const finalResult =
      sum > 0 ? (sum / balanceData?.salary_propotion) * 100 : 0;
    return finalResult;
  }, [balanceData]);
  const getConservationStatus = () => {
    if (!balanceData?.sum_expenses_invest_saving) return;

    if (valueConservation < 10) {
      return "bad";
    }
    return "good";
  };

  // การลงทุน
  const valueInvest = useMemo(() => {
    let sum = 0;
    if (balanceData?.sum_property_invest) {
      sum =
        balanceData?.net_wealth > 0
          ? (balanceData?.sum_property_invest / balanceData?.net_wealth) * 100
          : 0;
    }
    return sum;
  }, [balanceData]);
  const getInvestStatus = () => {
    if (!balanceData?.sum_property_invest) return;

    if (valueInvest > 50) {
      return "good";
    }
    return "bad";
  };

  const isMobile = useIsMobile();

  // วางแผนเกษียณ
  const getInvestSum = loopInvestmentAssets();
  const lastResultBF = getInvestSum[getInvestSum?.length - 1];
  const retirementAge = retireData?.age_retire;
  const retireBF = getInvestSum.find(
    (result) => result.age === retirementAge
  )?.results;
  const getremainBF = getInvestSum?.filter((thisRemain, i) => {
    if (thisRemain?.results <= 0) return { ...thisRemain };
  })?.[0];
  const remainBF = getremainBF?.age - 1;
  const beforeDiffYear = retireData?.age_retire - retireData?.age_current;
  const sumBeforeRetireInvest = useMemo(() => {
    const convertedMonth = beforeDiffYear * 12;
    const sumPMT = calPMT(
      retireData?.investment_bf_retirement / 100 / 12,
      convertedMonth,
      -lastResultBF?.results * -1
    );
    return sumPMT;
  }, [retireData]);
  const strategyChange = retireData?.strategy_sum ? true : false;

  const dataPlanAF = retireData?.af_investment_assets?.map(
    (af_investment_assets) => {
      return af_investment_assets?.results;
    }
  );

  const getInvestSumAF = useMemo(() => {
    return userData?.is_premium
      ? InvestmentReturnCalSensitive(
          retireData,
          strategy,
          balanceData,
          educationData
        )?.map((af_investment_assets, index) => {
          return {
            result: af_investment_assets?.asset_invest,
            age: retireData?.af_plan_age[index],
          };
        })
      : InvestmentReturnCalSensitiveNormal(retireData, strategy)?.map(
          (af_investment_assets, index) => {
            return {
              result: af_investment_assets?.asset_invest,
              age: retireData?.af_plan_age[index],
            };
          }
        );
  }, [retireData]);

  const retirementAgeAF = strategyLists?.includes("strategy_age_retire")
    ? retireData?.strategy_age_retire
    : retireData?.age_retire;

  const retireResult = getInvestSumAF.find(
    (result) => result.age === retirementAgeAF
  )?.result;

  const lastResultAF = getInvestSumAF[getInvestSumAF?.length - 1];
  const getremainAF = getInvestSumAF?.filter((thisRemain, i) => {
    if (thisRemain?.result <= 0) return { ...thisRemain };
  })?.[0];

  // here
  const remainAF = Number(getremainAF?.age) - 1;
  const afterDiffYear = Math.abs(
    retireData?.strategy_age_retire - retireData?.age_current
  );

  const afterDiffYearCheck =
    retireData?.strategy_age_retire - retireData?.age_current;

  const convertYearToMonth = (year) => {
    return year * 12;
  };

  const sumAfterRetireInvest = useMemo(() => {
      const convertedMonth = convertYearToMonth(afterDiffYear);
      const calPercent = strategyLists?.includes(
        "strategy_investment_bf_retirement_change"
      )
        ? retireData?.strategy_investment_bf_retirement_change
        : retireData?.investment_bf_retirement;
  
      const sumPMT = calPMTFVA(
        Math.abs(lastResultAF?.result),
        calPercent / 100 / 12,
        convertedMonth
      );
  
      return sumPMT;
    }, [retireData, strategy]);

  const getDetailSensitive = () => {
    return [
      {
        icon: (
          <ImageMain src="/images/assetdown.svg" className="w-4 h-4 mb-3" />
        ),
        render: () => {
          return (
            <div className="text-[10px] flex gap-1">
              <div className="   flex-none">
                สถานการณ์ที่ 1
                <div className="  ">
                  เศรษฐกิจถดถอย ผลตอบแทนการลงทุน
                  <div>
                    หลังเกษียณ เหลือเพียง{" "}
                    <span className=" font-semibold text-red300">1 %</span>
                  </div>
                </div>
              </div>
            </div>
          );
        },
      },
      {
        icon: (
          <ImageMain src="/images/heart_medical.svg" className="w-4 h-4 mb-3" />
        ),
        render: () => {
          return (
            <div className="text-[10px] flex gap-1">
              <div className="   flex-none">
                สถานการณ์ที่ 2
                <div className="  ">
                  เจ็บป่วยโรคร้ายแรงมีค่ารักษาพยาบาล
                  <div>
                    1 ล้านบาท ตอนอายุ{" "}
                    <span className=" font-semibold text-red300">
                      {" "}
                      {`${
                        retireData?.age_current
                          ? retireData?.age_current + 5
                          : "-"
                      } ปี`}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        },
      },
      {
        icon: <ImageMain src="/images/moneybag.svg" className="w-4 h-4 mb-3" />,
        render: () => {
          return (
            <div className="text-[10px] flex gap-1">
              <div className="   ">
                สถานการณ์ที่ 3
                <div className="   ">
                  ค้ำประกันให้ผู้อื่น เกิดหนี้ 5 ล้านบาท{" "}
                  <div>
                    ตอนอายุ{" "}
                    <span className=" font-semibold text-red300">
                      {`${
                        retireData?.age_current
                          ? retireData?.age_current + 10
                          : "-"
                      } ปี`}
                    </span>{" "}
                  </div>
                </div>
              </div>
            </div>
          );
        },
      },
    ];
  };

  const getDetailAF = () => {
    if (strategyChange) {
      if (lastResultInvest > 0) {
        return [
          {
            icon: <ICON_CHECK className=" w-[14px] h-[14px] text-maincyan" />,
            render: () => {
              return (
                <div className=" flex gap-1">
                  <div className="text-[10px] ">
                    {/* คาดการณ์สินทรัพย์เพื่อการลงทุน ณ อายุ */}
                    เริ่มวางแผนเกษียณตอนอายุ{" "}
                  </div>
                  <div
                    className={cn("text-[10px] font-semibold flex space-x-1")}
                  >
                    <div className="">
                      {_.isNaN(retireData?.age_current)
                        ? "-"
                        : retireData?.age_current}{" "}
                      ปี
                    </div>
                  </div>
                </div>
              );
            },
          },
          {
            icon: (
              <ICON_WARNING className=" w-[14px] h-[14px] text-mainyellow" />
            ),
            render: () => {
              return (
                <div className=" flex gap-1">
                  <div className="text-[10px] ">สามารถใช้จนได้ถึงอายุ</div>
                  <div className={cn("text-[10px] flex space-x-1")}>
                    <div className="font-semibold">
                      {retireData?.strategy_age_dead
                        ? retireData?.strategy_age_dead
                        : retireData?.age_dead || "-"}
                    </div>
                    <span className="text-mainblue font-normal">ปี</span>
                    <div>แต่อย่าประมาทนะ</div>
                  </div>
                </div>
              );
            },
          },
        ];
      }
      return [
        {
          icon: (
            <ICON_CHECK className=" w-[14px] h-[14px] text-maincyan flex-none" />
          ),
          render: () => {
            return (
              <div className=" flex gap-1">
                <div className="text-[10px] ">
                  {/* คาดการณ์สินทรัพย์เพื่อการลงทุน ณ อายุ{" "} */}
                  เริ่มวางแผนเกษียณตอนอายุ{" "}
                </div>
                <div className={cn("text-[10px] flex space-x-1")}>
                  <div className="font-semibold">
                    {_.isNaN(retireData?.age_current)
                      ? "-"
                      : retireData?.age_current}{" "}
                    ปี
                  </div>
                </div>
              </div>
            );
          },
        },
        {
          icon: <ICON_WARNING className=" w-[14px] h-[14px] text-mainyellow" />,
          render: () => {
            return (
              <div className=" flex flex-col">
                <div className="flex gap-1">
                  <div className="text-[10px] ">สามารถใช้จนได้ถึงอายุ</div>
                  <div className={cn("text-[10px] flex space-x-1")}>
                    <div className="font-semibold">
                      {_.isNaN(remainAF) ? "-" : remainAF} ปี
                    </div>
                  </div>
                </div>
              </div>
            );
          },
        },
        {
          icon: <ICON_CLOSE className=" w-[14px] h-[14px] text-maincyan" />,
          render: () => {
            return (
              <div className="flex flex-col">
                <div className=" flex gap-1">
                  <div className="text-[10px] ">
                    ถ้าต้องการมีเงินใช้จ่ายจนถึงอายุ{" "}
                  </div>
                  <div className={cn("text-[10px] font-semibold")}>
                    {_.isNaN(retireData?.age_dead) ? "-" : retireData?.age_dead}{" "}
                    ปี
                  </div>
                </div>
                <div>
                  <div className="text-[10px] ">
                    ยังขาดอยู่ทั้งหมด{" "}
                    <span className=" font-medium text-black">
                      {lastResultAF?.result < 0
                        ? numeral(lastResultAF?.result * -1)?.format("0,0")
                        : numeral(lastResultAF?.result)?.format("0,0")}
                      {""} บาท
                    </span>
                  </div>
                </div>
              </div>
            );
          },
        },
        {
          icon: <ICON_STAR className=" w-[14px] h-[14px] text-maingreen" />,
          render: () => {
            if (afterDiffYearCheck !== 0) {
              return (
                <div className="flex flex-col">
                  <div className=" flex gap-1">
                    <div className="text-[10px] ">
                      ควรวางแผนลงทุนผลตอบแทน{" "}
                    </div>
                    <div className={cn("text-[10px] font-semibold")}>
                      {" "}
                      {strategyLists?.includes(
                        "strategy_investment_bf_retirement_change"
                      )
                        ? Math.abs(
                            retireData?.strategy_investment_bf_retirement_change
                          )
                        : Math.abs(retireData?.investment_bf_retirement)}{" "}
                      % 
                    </div>
                  </div>
                  <div className="text-[10px] flex space-x-1">
                    <div>เป็นระยะเวลา</div>
                    <span className=" font-semibold">
                      {_.isNaN(afterDiffYear) ? "-" : afterDiffYear} ปี
                    </span>{" "}
                    <div>เดือนละ</div>
                    <span className=" font-semibold text-mainblue">
                      {numeral(sumAfterRetireInvest)?.format("0,0")} บาท
                    </span>
                  </div>
                </div>
              );
            }

            return (
              <div className="flex flex-col">
                <div className="  text-[10px] ">
                  {`อย่าประมาทในการวางแผนเกษียณ ตอนคุณอายุ `}
                  <span className="font-semibold">
                    {_.isNaN(remainAF) ? "-" : remainAF} ปี
                  </span>
                  {` รายรับของคุณจะไม่พอใช้ ควรขยายเวลาเกษียณ หรือรีบหารายได้เพิ่มเติม`}
                </div>
              </div>
            );
          },
        },
      ];
    } else {
      if (lastResultAF?.result > 0) {
        return [
          {
            icon: <ICON_CHECK className=" w-[14px] h-[14px] text-maincyan" />,
            render: () => {
              return (
                <div className="text-[10px] flex flex-col gap-1">
                  <div className=" ">
                    คาดการณ์สินทรัพย์เพื่อการลงทุน ณ วันเกษียณอายุ
                  </div>
                  <div className="flex">
                    <span className="font-medium mr-1">
                      {_.isNaN(retireData?.age_retire)
                        ? "-"
                        : retireData?.age_retire}{" "}
                      ปี
                    </span>
                    <div className="flex space-x-1">
                      <div>จำนวน</div>
                      <span className=" font-medium text-mainblue">
                        {numeral(retireBF)?.format("0,0.00")}
                      </span>
                    </div>
                  </div>
                </div>
              );
            },
          },
          {
            icon: (
              <ICON_WARNING className=" w-[14px] h-[14px] text-mainyellow" />
            ),
            render: () => {
              return (
                <div className=" flex gap-1">
                  <div className="text-[10px] ">สามารถใช้จนได้ถึงอายุ</div>
                  <div className={cn("text-[10px] flex space-x-1")}>
                    <div className="font-semibold">
                      {_.isNaN(retireData?.age_dead)
                        ? "-"
                        : retireData?.age_dead}
                    </div>
                    <span className="text-mainblue font-normal">ปี</span>
                    <div>แต่อย่าประมาทนะ</div>
                  </div>
                </div>
              );
            },
          },
        ];
      }
      return [
        {
          icon: <ICON_WARNING className=" w-[14px] h-[14px] text-mainyellow" />,
          render: () => {
            return (
              <div className=" flex gap-1">
                <div className="text-[10px] ">สามารถใช้จนได้ถึงอายุ</div>
                <div className={cn("text-[10px] flex space-x-1")}>
                  <div className="font-semibold">
                    {" "}
                    {_.isNaN(remainAF) ? "-" : remainAF} ปี
                  </div>
                </div>
              </div>
            );
          },
        },
        {
          icon: <ICON_CLOSE className=" w-[14px] h-[14px] text-mainred" />,
          render: () => {
            return (
              <div className="flex flex-col">
                <div className=" flex gap-1 flex-wrap ">
                  <div className="text-[10px] ">
                    ถ้าต้องการมีเงินใช้จ่ายจนถึงอายุ
                  </div>
                  <div className={cn("text-[10px] flex space-x-1")}>
                    <div className="font-semibold">
                      {_.isNaN(retireData?.age_dead)
                        ? "-"
                        : retireData?.age_dead}{" "}
                      ปี
                    </div>
                  </div>
                </div>
                <div className="flex gap-1 text-[10px]">
                  <div className="  text-[10px]">ยังขาดอยู่ทั้งหมด</div>
                  <div className={cn("text-[10px] font-semibold text-black")}>
                    {_.isNaN(lastResultAF?.result)
                      ? "-"
                      : numeral(
                          lastResultAF?.result < 0
                            ? lastResultAF?.result * -1
                            : lastResultAF?.result
                        ).format("0,0")}{" "}
                    บาท
                  </div>
                </div>
              </div>
            );
          },
        },
        {
          icon: <ICON_STAR className=" w-[14px] h-[14px] text-maincyan" />,
          render: () => {
            return (
              <div className="flex flex-col">
                <div className=" flex gap-1">
                  <div className="text-[10px] ">ควรวางแผนลงทุนผลตอบแทน</div>
                </div>
                <div className={cn("text-[10px] font-semibold")}>
                  {retireData?.investment_bf_retirement < 0
                    ? "3"
                    : retireData?.investment_bf_retirement}{" "}
                  %<span className=" font-normal"> เป็นระยะเวลา </span>
                  <span className="font-semibold">
                    {_.isNaN(afterDiffYear) ? "-" : afterDiffYear} ปี{" "}
                  </span>
                  <span className="font-normal">เดือนละ </span>
                  <span className="text-mainblue">
                    {_.isNaN(sumAfterRetireInvest)
                      ? "-"
                      : numeral(sumAfterRetireInvest)?.format("0,0")}
                  </span>
                </div>
              </div>
            );
          },
        },
      ];
    }
  };

  const retireSensitiveData = loopInvestmentAssetsAFSensitive();
  const dataPlanSensitive = () => {
    if (userData?.is_premium) {
      return InvestmentReturnCalSensitive(
        retireData,
        strategy,
        balanceData
      )?.map((af_investment_assets) => {
        return af_investment_assets?.asset_invest;
      });
    }

    return InvestmentReturnCalSensitiveNormal(retireData, strategy)?.map(
      (af_investment_assets) => {
        return af_investment_assets?.asset_invest;
      }
    );
  };

  const getInvestSumSen = useMemo(() => {
    return userData?.is_premium
      ? InvestmentReturnCalSensitive(
          retireData,
          strategy,
          balanceData,
          educationData
        )?.map((af_investment_assets, index) => {
          return {
            result: af_investment_assets?.asset_invest,
            age: retireData?.af_plan_age[index],
          };
        })
      : InvestmentReturnCalSensitiveNormal(retireData, strategy)?.map(
          (af_investment_assets, index) => {
            return {
              result: af_investment_assets?.asset_invest,
              age: retireData?.af_plan_age[index],
            };
          }
        );
  }, [retireData]);

  const lastResultSen = getInvestSumSen[getInvestSumSen?.length - 1];

  const lastResultInvest = lastResultSen?.result;

  // หาอายุสุดท้าย
  const getAgeSensitive = retireSensitiveData?.filter((item) => {
    if (item?.results > 0) return item;
  });
  const ageSensitive = getAgeSensitive?.[getAgeSensitive?.length - 1]?.age;

  const weightValue = useMemo(
    // let avgWeight = (3 * 10000 + 6000 * 9) / (10000 + 6000);
    () =>
      avgWeight(
        Number(retireData?.investment_bf_retirement),
        Number(retireData?.investment_assets),
        Number(retireData?.strategy_assets_generate),
        Number(retireData?.strategy_assets_generate_per_year)
      ),
    [
      Number(retireData?.investment_bf_retirement),
      Number(retireData?.investment_assets),
      Number(retireData?.strategy_assets_generate),
      Number(retireData?.strategy_assets_generate_per_year),
    ]
  );

  const getFormattedValue = (row) => {
    if (row?.key === "strategy_out_come") {
      return userData?.is_premium
        ? numeral(retireData?.out_come_variable)?.format("0,0")
        : numeral(retireData?.out_come)?.format("0,0");
    } else if (row?.key === "strategy_income") {
      return numeral(retireData?.salary)?.format("0,0");
    } else if (row?.key === "strategy_passive_income_retire") {
      return numeral(retireData?.pension)?.format("0,0");
    } else if (row?.key === "strategy_assets_generate") {
      return numeral(retireData?.investment_assets)?.format("0,0");
    } else if (row?.key === "strategy_assets_generate_per_year") {
      return numeral(retireData?.investment_bf_retirement)?.format("0,0");
    } else if (row?.key === "strategy_salary") {
      return numeral(retireData?.salary)?.format("0,0");
    } else if (row?.key === "strategy_salary_peryear") {
      return numeral(retireData?.salary_peryear)?.format("0,0");
    } else if (row?.key === "strategy_control_rate_outcome") {
      return numeral(retireData?.out_come_peryear)?.format("0,0");
    } else if (row?.key === "strategy_age_retire") {
      return retireData?.age_retire;
    } else if (row?.key === "strategy_age_dead") {
      return retireData?.age_dead;
    } else if (row?.key === "strategy_investment_bf_retirement") {
      return retireData?.investment_bf_retirement;
    } else if (row?.key === "strategy_investment_af_retirement") {
      return retireData?.investment_af_retirement;
    } else if (row?.key === "strategy_investment_bf_retirement_change") {
      return retireData?.investment_bf_retirement;
    } else if (row?.key === "strategy_investment_af_retirement_change") {
      return retireData?.investment_af_retirement;
    }
    return 0;
  };

  const getFormattedValueAfter = (row) => {
    if (row?.key === "strategy_out_come") {
      return userData?.is_premium
        ? numeral(
            retireData?.out_come_variable - retireData?.strategy_out_come
          )?.format("0,0")
        : numeral(retireData?.out_come - retireData?.strategy_out_come)?.format(
            "0,0"
          );
    } else if (row?.key === "strategy_income") {
      return numeral(retireData?.salary + retireData?.strategy_income)?.format(
        "0,0"
      );
    } else if (row?.key === "strategy_passive_income_retire") {
      return numeral(
        retireData?.pension + retireData?.strategy_passive_income_retire
      )?.format("0,0");
    } else if (row?.key === "strategy_assets_generate") {
      return numeral(
        retireData?.strategy_assets_generate + retireData?.investment_assets
      )?.format("0,0");
    } else if (row?.key === "strategy_assets_generate_per_year") {
      // return weightValue;
      return numeral(retireData?.strategy_assets_generate_per_year)?.format(
        "0,0"
      );
    } else if (row?.key === "strategy_salary") {
      return numeral(retireData?.strategy_salary)?.format("0,0");
    } else if (row?.key === "strategy_salary_peryear") {
      return numeral(retireData?.strategy_salary_peryear)?.format("0,0");
    } else if (row?.key === "strategy_control_rate_outcome") {
      return numeral(retireData?.strategy_control_rate_outcome)?.format("0,0");
    } else if (row?.key === "strategy_age_retire") {
      return retireData?.strategy_age_retire;
    } else if (row?.key === "strategy_age_dead") {
      return retireData?.strategy_age_dead;
    } else if (row?.key === "strategy_investment_bf_retirement") {
      return retireData?.strategy_investment_bf_retirement;
    } else if (row?.key === "strategy_investment_af_retirement") {
      return retireData?.strategy_investment_af_retirement;
    } else if (row?.key === "strategy_investment_bf_retirement_change") {
      return retireData?.strategy_investment_bf_retirement_change;
    } else if (row?.key === "strategy_investment_af_retirement_change") {
      return retireData?.strategy_investment_af_retirement_change;
    }
    return 0;
  };

  const getIcon = (row) => {
    if (row?.key === "strategy_out_come") {
      return "../images/stg_1.png";
    } else if (row?.key === "strategy_income") {
      return "../images/saving_expenses.png";
    } else if (row?.key === "strategy_passive_income_retire") {
      return "../images/stg_2.png";
    } else if (row?.key === "strategy_assets_generate") {
      return "../images/saving_save.png";
    } else if (row?.key === "strategy_assets_generate_per_year") {
      return "../images/saving_save.png";
    } else if (row?.key === "strategy_salary") {
      return "../images/stg_3.png";
    } else if (row?.key === "strategy_salary_peryear") {
      return "../images/stg_3.png";
    } else if (row?.key === "strategy_control_rate_outcome") {
      return "../images/stg_1.png";
    } else if (row?.key === "strategy_age_retire") {
      return "../images/stg_4.png";
    } else if (row?.key === "strategy_age_dead") {
      return "../images/stg_5.png";
    } else if (row?.key === "strategy_investment_bf_retirement") {
      return "../images/stg_4.png";
    } else if (row?.key === "strategy_investment_af_retirement") {
      return "../images/stg_4.png";
    } else if (row?.key === "strategy_investment_bf_retirement_change") {
      return "../images/stg_7.png";
    } else if (row?.key === "strategy_investment_af_retirement_change") {
      return "../images/stg_7.png";
    }
  };

  const getETC = (row) => {
    if (row?.key === "strategy_assets_generate_per_year") {
      return (
        <div className=" text-xs w-full border-t border-gray-300 text-maingray">
          <div className=" mt-3 mb-1">
            *อัตราผลตอบแทนที่ใช้คำนวณ {weightValue}
          </div>
          <div>
            ** (สินทรัพย์มีไว้ลงทุน x อัตราผลตอบแทนก่อนเกษียณ) +
            (แปลงสินทรัพย์ให้มีผลตอบแทน x อัตราผลตอบแทนที่คาดหวัง) /
            (สินทรัพย์มีไว้ลงทุน + แปลงสินทรัพย์ให้มีผลตอบแทน){" "}
          </div>
        </div>
      );
    }
  };

  const keyUnit = [
    "strategy_out_come",
    "strategy_income",
    "strategy_passive_income_retire",
    "strategy_salary",
    "strategy_assets_generate",
  ];

  const keyUnitPercent = [
    "strategy_assets_generate",
    "strategy_assets_generate_per_year",
    "strategy_salary_peryear",
    "strategy_control_rate_outcome",
    "strategy_investment_bf_retirement",
    "strategy_investment_af_retirement",
    "strategy_investment_bf_retirement_change",
    "strategy_investment_af_retirement_change",
  ];

  const getUnit = (row) => {
    return keyUnit?.includes(row?.key)
      ? "บาท"
      : keyUnitPercent?.includes(row?.key)
      ? "%"
      : "ปี";
  };

  return (
    <div className={cn(STYLE_PAGE)}>
      <PdfHeader />
      <PdfLine />
      <PdfHeaderText title="วางแผนเกษียณ" icon="/images/grandparents.svg" />
      {/* ทดสอบความอ่อนไหว */}
      <PdfCard title={"ทดสอบความอ่อนไหว"}>
        <PdfCardWhite
          className={"flex flex-col px-3 items-center justify-center"}
        >
          <div className={cn(STYLE_PDF_TEXT_MEDIUM_11)}>
            ความเคลื่อนไหวทางการเงิน
          </div>
          <div className="flex space-x-2">
            <div className={cn("w-[230px] mr-4")}>
              <LineChartMain
                axisSize={6}
                labels={dataAgeAF()}
                dataPlan={dataPlanSensitive()}
                fontSize="text-[10px]"
                head={``}
                height={150}
                width={250}
                classNameBath={"md:top-[10%]"}
                classNameAge={"right-[-4%] md:right-[0%]"}
              />
            </div>

            <div className="flex flex-col">
              {lastResultInvest > 0 ? (
                <div className=" flex flex-col justify-center mt-[18px]">
                  <div className=" flex  justify-center items-center gap-[3px]">
                    <ImageMain
                      src="../images/taxicon3.png"
                      className="w-6 h-6"
                    />
                    <div className=" text-mainblue font-semibold text-[13px]">
                      ยอดเยี่ยม
                    </div>
                  </div>
                  <div className=" text-mainblue text-center text-[11px] mt-1">
                    ถ้าเกิดสถานการณ์เหล่านี้ขึ้นจริง
                    <div> การปรับกลยุทธ์เกษียณของคุณเป็นแผนที่ยอดเยี่ยม</div>
                    <div>แต่ก็อย่าประมาทล่ะ</div>
                  </div>
                </div>
              ) : (
                <div className=" flex flex-col justify-center mt-1">
                  <div className=" flex justify-center items-center gap-[3px]">
                    <ImageMain
                      src="../images/badface_2.png"
                      className="w-6 h-6"
                    />
                    <div className=" text-mainblue font-semibold text-[13px]">
                      ยังไม่ปลอดภัย
                    </div>
                  </div>
                  <div className=" text-mainblue text-center text-[11px] mt-1">
                    ถ้าเกิดสถานการณ์เหล่านี้ขึ้นจริง
                    <div>การปรับกลยุทธ์เกษียณของคุณยังไม่ปลอดภัยเท่าที่ควร</div>
                  </div>
                </div>
              )}

              <div className="mt-2 space-y-1">
                {getDetailAF()?.map((item, index) => {
                  return (
                    <div key={index} className="flex gap-2.5">
                      <div>{item?.icon}</div>
                      {item?.render && item?.render()}
                    </div>
                  );
                })}
              </div>

              <div className=" flex flex-col justify-center mt-1">
                <div className=" flex justify-center items-center gap-[3px]">
                  <div className=" text-mainblue font-semibold text-[13px]">
                    สถานการณ์สมมติ
                  </div>
                </div>
              </div>
              <div className="mt-2 space-y-1 flex flex-col items-start justify-center">
                {getDetailSensitive()?.map((item, index) => {
                  if (retireData?.check_imagine_one && index === 0) {
                    return (
                      <div key={index} className="flex items-center gap-2.5">
                        <div>{item?.icon}</div>
                        {item?.render && item?.render()}
                      </div>
                    );
                  }

                  if (retireData?.check_imagine_two && index === 1) {
                    return (
                      <div key={index} className="flex items-center gap-2.5">
                        <div>{item?.icon}</div>
                        {item?.render && item?.render()}
                      </div>
                    );
                  }

                  if (retireData?.check_imagine_three && index === 2) {
                    return (
                      <div key={index} className="flex items-center gap-2.5">
                        <div>{item?.icon}</div>
                        {item?.render && item?.render()}
                      </div>
                    );
                  }
                })}
              </div>
            </div>
          </div>
        </PdfCardWhite>
      </PdfCard>

      <PdfFooter page={page} />
    </div>
  );
}

export default PdfPageRetireThree;
